import { useCardSessionControls } from '@littlebird/action-cards-view/react';
import { Fragment, type ReactNode } from 'react';

import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import { type CardOpen } from '../cardOpen';
import { formatRelativeTime } from '../registry/internals/localTime';

import { appGlyphOf } from './FeedOpenLink';
import { FEED_OPEN_LINK_CLASS } from './feedStyles';

import type { CardState } from '@littlebird/action-cards-view';

type FeedSourceChipProps = {
  icon: ReactNode;
  appName: string;
  origin: CardState['origin'];
  // The card's age as an ISO time, or a day label History already wrote
  age?: string | null;
  day?: string;
  openUrl: string | null;
  onOpenUrl: CardOpen;
  onOpenSource?: () => void;
};

// The app's glyph at the chip's size, without its tile or status badge (feed.css)
const CHIP_TILE_CLASS =
  'flex shrink-0 [--ac-row-glyph:calc(14*var(--ac-u))] [--ac-row-tile:calc(16*var(--ac-u))]';

const SOURCE_LINK_CLASS =
  'rounded-sm underline decoration-border-strongest decoration-dotted underline-offset-[3px] focus-ring hover:text-foreground';

// "Product meeting". The backend names a meeting with no title "Untitled meeting", which takes no second "meeting"
export const meetingText = (meetingName: string) =>
  /\bmeeting$/i.test(meetingName) ? meetingName : `${meetingName} meeting`;

// Whether a card without a link has a source line to show
export const hasFeedSourceLine = (
  origin: CardState['origin'],
  age?: string | null
): boolean => Boolean(origin?.prefix || origin?.label || origin?.detail || age);

// Where the card came from and when, at the right of the open card's action band: the app to open it in when it
// has a link, else the source itself
export const FeedSourceChip = ({
  icon,
  appName,
  origin,
  age,
  day,
  openUrl,
  onOpenUrl,
  onOpenSource,
}: FeedSourceChipProps) => {
  // The chip renders in the card's action band, so its open saves what the user typed first
  const { saveEdits } = useCardSessionControls();
  const AppGlyph = appGlyphOf(appName);
  const when = day ?? (age ? formatRelativeTime(age) : undefined);

  if (openUrl)
    return (
      <Tooltip label={`Open in ${appName}`}>
        <a
          href={openUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open in ${appName}`}
          data-feed-source=""
          className={cn(
            FEED_OPEN_LINK_CLASS,
            AppGlyph && 'w-[calc(28*var(--ac-u))] justify-center px-0'
          )}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenUrl(openUrl, saveEdits);
          }}
        >
          {/* An app Littlebird knows the mark of shows the mark alone; any other names itself */}
          {AppGlyph ? (
            <AppGlyph
              aria-hidden="true"
              className="size-[calc(15*var(--ac-u))] shrink-0"
            />
          ) : (
            <>
              <span aria-hidden="true" className={CHIP_TILE_CLASS}>
                {icon}
              </span>
              <span className="truncate">{appName}</span>
            </>
          )}
        </a>
      </Tooltip>
    );

  const pieces: ReactNode[] = [
    origin?.prefix,
    origin?.label && onOpenSource ? (
      <button
        type="button"
        className={SOURCE_LINK_CLASS}
        onClick={onOpenSource}
      >
        {origin.label}
      </button>
    ) : (
      origin?.label
    ),
    origin?.detail,
    when,
  ].filter(Boolean);
  if (!pieces.length) return null;

  return (
    <span
      data-feed-source=""
      className="relative z-[1] flex min-w-0 items-center gap-[calc(6*var(--ac-u))] whitespace-nowrap text-[length:calc(12*var(--ac-u))] text-foreground-muted"
    >
      <span aria-hidden="true" className={CHIP_TILE_CLASS}>
        {icon}
      </span>
      <span className="truncate">
        {pieces.map((piece, index) => (
          <Fragment key={index}>
            {index > 0 ? ' · ' : null}
            {piece}
          </Fragment>
        ))}
      </span>
    </span>
  );
};

const MENU_SOURCE_CLASS =
  'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left outline-none';

// Where the card came from, at the top of the open card's ⋯ menu: the source on the first line, then how it came
// and when. A source the app can open makes the whole row a menu item
export const FeedSourceMenuItem = ({
  icon,
  origin,
  age,
  onOpenSource,
}: Pick<FeedSourceChipProps, 'icon' | 'origin' | 'age' | 'onOpenSource'>) => {
  const when = age ? formatRelativeTime(age) : undefined;
  const title = origin?.label ?? origin?.prefix;
  const meta = [origin?.label ? origin.prefix : null, origin?.detail, when]
    .filter(Boolean)
    .join(' · ');
  const body = (
    <>
      <span aria-hidden="true" className="ac-scale mt-0.5 flex shrink-0">
        <span className={CHIP_TILE_CLASS}>{icon}</span>
      </span>
      <span className="flex min-w-0 flex-col">
        {title ? (
          <span className="whitespace-normal break-words text-sm text-foreground">
            {title}
          </span>
        ) : null}
        {meta ? (
          <span className="whitespace-normal break-words text-xs text-foreground-muted">
            {meta}
          </span>
        ) : null}
      </span>
    </>
  );

  return origin?.label && onOpenSource ? (
    <button
      type="button"
      data-feed-source=""
      className={cn(
        MENU_SOURCE_CLASS,
        'transition-colors duration-[120ms] hover:bg-surface-hover focus-visible:bg-surface-hover motion-reduce:transition-none'
      )}
      onClick={onOpenSource}
    >
      {body}
    </button>
  ) : (
    <div data-feed-source="" className={MENU_SOURCE_CLASS}>
      {body}
    </div>
  );
};
